import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-heading text-foreground-900 mb-3">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name} - caoliushequ官方导航`}
        description={`caoliushequ${category.description}，浏览caoliushequ${category.name}相关全部内容。`}
        keywords={`caoliushequ,${category.name},caoliushequ${category.name}`}
        canonical={`https://caolnet.github.io/category/${slug}`}
        ogTitle={`${category.name} - caoliushequ官方导航`}
        ogDescription={`caoliushequ${category.description}`}
        ogUrl={`https://caolnet.github.io/category/${slug}`}
      />

      <div className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category.name },
              ]}
            />
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
              {category.name}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              {category.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            <Link
              to="/"
              className="px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              全部
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  c.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${c.icon} mr-1.5 text-xs`} />
                {c.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200 transition-colors block cursor-pointer"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={`${article.title} - caoliushequ`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-600">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    </div>
                    <h2 className="text-base font-semibold text-foreground-800 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line" /> {article.readCount.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line" /> {article.commentCount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}